import { useState, useCallback } from 'react';
import errorTrackingService from '../services/errorTrackingService';

// Custom hook for managing error state in components
export const useErrorState = (screen, initialContext = {}) => {
  const [errors, setErrors] = useState([]);
  const [retryCallbacks, setRetryCallbacks] = useState({});

  // Add error with retry callback
  const addError = useCallback((error, context = {}, retryCallback = null) => {
    const errorEntry = errorTrackingService?.captureError(error, {
      ...initialContext,
      ...context,
      screen
    });

    setErrors(prev => {
      // Check if similar error already exists
      const existingIndex = prev?.findIndex(
        e => e?.code === errorEntry?.code && 
             e?.table === errorEntry?.table && 
             e?.operation === errorEntry?.operation
      );

      if (existingIndex >= 0) {
        // Update existing error
        const updated = [...prev];
        updated[existingIndex] = errorEntry;
        return updated;
      }

      // Add new error
      return [...prev, errorEntry];
    });

    // Store retry callback
    if (retryCallback) {
      setRetryCallbacks(prev => ({
        ...prev,
        [errorEntry?.id]: retryCallback
      }));
    }

    return errorEntry?.id;
  }, [screen, initialContext]);

  // Remove specific error
  const removeError = useCallback((errorId) => {
    setErrors(prev => prev?.filter(e => e?.id !== errorId));
    setRetryCallbacks(prev => {
      const updated = { ...prev };
      delete updated?.[errorId];
      return updated;
    });
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
    setRetryCallbacks({});
  }, []);

  // Retry specific error
  const retryError = useCallback(async (errorId) => {
    const callback = retryCallbacks?.[errorId];
    if (callback) {
      try {
        await callback();
        // If successful, remove the error
        removeError(errorId);
      } catch (err) {
        // If retry fails, update the error
        const error = errors?.find(e => e?.id === errorId);
        if (error) {
          addError(err, {
            table: error?.table,
            operation: error?.operation,
            action: `Retry of ${error?.action}`
          }, callback);
        }
      }
    }
  }, [retryCallbacks, errors, removeError, addError]);

  return {
    errors,
    addError,
    removeError,
    clearErrors,
    retryError,
    hasErrors: errors?.length > 0
  };
};

// Format error message for display
export const formatErrorMessage = (error) => {
  const errorMessages = {
    '42P17': 'Database policy configuration error (infinite recursion)',
    '42501': 'Permission denied - you don\'t have access to this resource',
    'PGRST301': 'Row level security policy violation',
    'PGRST204': 'No data found or access denied',
    '23505': 'Duplicate entry - this record already exists',
    '23503': 'Related record not found',
    '22P02': 'Invalid data format',
    'PGRST116': 'Invalid query parameters',
    '08006': 'Connection failed',
    '08003': 'Connection does not exist',
    '57014': 'Query timeout - operation took too long'
  };

  const humanReadable = errorMessages?.[error?.code] || error?.message;
  
  return {
    title: `Error ${error?.code || 'UNKNOWN'}`,
    message: humanReadable,
    technical: error?.message,
    canRetry: !['42P17', '42501', 'PGRST301']?.includes(error?.code) // Don't retry RLS errors
  };
};

// Get error icon based on error type
export const getErrorIcon = (error) => {
  if (error?.isRLSError) return '🔒';
  if (error?.code?.startsWith('23')) return '⚠️';
  if (error?.code?.startsWith('08')) return '🔌';
  if (error?.code?.startsWith('57')) return '⏱️';
  return '❌';
};

// Get error color based on severity
export const getErrorSeverity = (error) => {
  if (error?.isRLSError) return 'critical';
  if (error?.code?.startsWith('23')) return 'warning';
  if (error?.code?.startsWith('08')) return 'error';
  return 'error';
};